import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Calendar } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { developers, locations } from "@/data/mock";

const PropertyCard = ({ property, index = 0 }) => {
  const { t, lang, formatPrice } = useApp();
  const dev = developers.find((d) => d.id === property.developer);
  const loc = locations.find((l) => l.id === property.location);
  const name = lang === "ar" ? property.name_ar : property.name_en;
  const locName = loc ? (lang === "ar" ? loc.name_ar : loc.name_en) : "";

  return (
    <article
      data-testid={`property-card-${property.id}`}
      className="group bg-[#1E2235]/40 border border-[#C9A84C]/10 hover:border-[#C9A84C]/40 transition-all duration-700 flex flex-col"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <Link to={`/property/${property.id}`} className="img-zoom relative aspect-[4/3] overflow-hidden block">
        <img
          src={property.image}
          alt={name}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E1A] via-transparent to-transparent opacity-60" />
        {property.hot && (
          <span className="absolute top-4 start-4 bg-[#C9A84C] text-[#0A0E1A] text-[10px] tracking-[0.2em] uppercase font-medium px-3 py-1">
            Hot
          </span>
        )}
        <span className="absolute bottom-4 end-4 text-[10px] tracking-[0.2em] uppercase text-[#C9A84C] border border-[#C9A84C]/50 px-3 py-1 bg-[#0A0E1A]/70 backdrop-blur-sm">
          {dev?.name}
        </span>
      </Link>

      <div className="p-7 flex-1 flex flex-col">
        <h3 className="font-display text-2xl leading-tight text-[#F5F0E8] group-hover:text-[#C9A84C] transition-colors">
          {name}
        </h3>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#A0A5B5]">
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-3 h-3 text-[#C9A84C]" /> {locName}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3 h-3 text-[#C9A84C]" /> {t("handover")} {property.handover}
          </span>
        </div>

        <div className="gold-divider my-5" />

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#A0A5B5]">
              {t("starting_from")}
            </p>
            <p className="font-display text-xl text-[#C9A84C] mt-1">
              {formatPrice(property.startingPrice)}
            </p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#A0A5B5]">
              {t("roi_estimate")}
            </p>
            <p className="font-display text-xl text-[#F5F0E8] mt-1">{property.roi}</p>
          </div>
        </div>

        <p className="text-xs text-[#A0A5B5] font-light leading-relaxed mb-6 line-clamp-2">
          <span className="text-[#F5F0E8]/80">{t("payment_plan")}:</span> {property.paymentPlan}
        </p>

        <Link
          to={`/property/${property.id}`}
          data-testid={`property-card-cta-${property.id}`}
          className="mt-auto inline-flex items-center justify-between border-t border-[#C9A84C]/20 pt-5 text-xs tracking-[0.18em] uppercase font-medium text-[#F5F0E8] hover:text-[#C9A84C] transition-colors"
        >
          {t("request_details")}
          <ArrowUpRight className="w-4 h-4 ltr-flip" />
        </Link>
      </div>
    </article>
  );
};

export default PropertyCard;